import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  CreditCard, Search, Calendar, Download, RefreshCw, Filter,
  Building2, DollarSign, ArrowUpRight, ArrowDownRight, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

export default function TransactionsPage() {
  const { token } = useOutletContext();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [restaurantFilter, setRestaurantFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [loadError, setLoadError] = useState(false);

  const fetchTransactions = useLatestCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(`${API}/company-admin/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Transactions fetch error:', error);
      toast.error('Could not load transactions');
      setTransactions([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchTransactions();
  }, [token, fetchTransactions]);

  const isSuccessful = (t) => t.status === 'completed' || t.status === 'paid';
  const summary = {
    total: transactions.length,
    successful: transactions.filter(isSuccessful).length,
    failed: transactions.filter(t => t.status === 'failed').length,
    totalAmount: transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
  };

  const handleExport = () => {
    if (filteredTransactions.length === 0) {
      toast.error('No transactions to export');
      return;
    }
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['ID', 'Restaurant', 'Customer', 'Method', 'Status', 'Date', 'Amount'];
    const rows = filteredTransactions.map(t => [
      t.id, t.restaurant_name, t.customer_name, t.payment_method, t.status,
      t.created_at || '', (Number(t.amount) || 0).toFixed(2)
    ]);
    const csv = [header, ...rows].map(r => r.map(esc).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredTransactions = transactions.filter(t => {
    if (restaurantFilter !== 'all' && t.restaurant_name !== restaurantFilter) return false;
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        t.customer_name?.toLowerCase().includes(query) ||
        t.restaurant_name?.toLowerCase().includes(query) ||
        t.id?.toString().includes(query)
      );
    }
    return true;
  });

  const statusColors = {
    completed: 'bg-green-500/20 text-green-400',
    pending: 'bg-yellow-500/20 text-yellow-400',
    failed: 'bg-red-500/20 text-red-400'
  };

  const restaurants = [...new Set(transactions.map(t => t.restaurant_name))];

  return (
    <div className="space-y-6 p-4 sm:p-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="company-transactions">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">All Transactions</h1>
          <p className="text-slate-400">Platform-wide payment history</p>
        </div>
        <Button size="sm" onClick={handleExport} className="bg-gradient-to-r from-yellow-500 to-orange-500">
          <Download className="h-4 w-4 mr-2" />
          Export
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400">Total Transactions</p>
            <p className="text-2xl font-bold text-white">{summary.total}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400">Total Amount</p>
            <p className="text-2xl font-bold text-green-400">${summary.totalAmount.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400">Successful</p>
            <p className="text-2xl font-bold text-green-400">{summary.successful}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-4">
            <p className="text-sm text-slate-400">Failed</p>
            <p className="text-2xl font-bold text-red-400">{summary.failed}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Select value={restaurantFilter} onValueChange={setRestaurantFilter}>
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="All Restaurants" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Restaurants</SelectItem>
                {restaurants.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
            </div>
          ) : loadError ? (
            <div className="text-center py-16" data-testid="company-transactions-error">
              <p className="text-slate-300">Could not load transactions.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={fetchTransactions}>Try again</Button>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-16 text-slate-400">No transactions</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">ID</TableHead>
                  <TableHead className="text-slate-400">Restaurant</TableHead>
                  <TableHead className="text-slate-400">Customer</TableHead>
                  <TableHead className="text-slate-400">Method</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400 text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map(txn => (
                  <TableRow key={txn.id} className="border-slate-700 hover:bg-slate-750">
                    <TableCell className="font-mono text-white">#{txn.id}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-slate-300">
                        <Building2 className="h-4 w-4 text-slate-500" />
                        {txn.restaurant_name}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-300">{txn.customer_name}</TableCell>
                    <TableCell>
                      {txn.payment_method ? (
                        <Badge variant="outline" className="border-slate-600 text-slate-300 capitalize">
                          {txn.payment_method.replace('_', ' ')}
                        </Badge>
                      ) : <span className="text-slate-500">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[txn.status]}>{txn.status}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-400">
                      {new Date(txn.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-white">
                      ${txn.amount?.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
